import * as THREE from 'three';

/**
 * One bounded planar reflection for the lake. All water and presentation glass
 * must be excluded. The target uses linear HDR colour and transparent coverage;
 * the water shader fills empty pixels with sky. No render hooks / recursion.
 * A staging target protects the last valid image from muted or failed probes.
 */
export function createWaterReflection({renderer, scene, camera, uniforms, exclude = []}) {
  const createTarget = () => new THREE.WebGLRenderTarget(1, 1, {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    depthBuffer: true,
    stencilBuffer: false,
    generateMipmaps: false,
    samples: 0,
  });
  const targets = [createTarget(), createTarget()];
  for (const [i, target] of targets.entries()) {
    target.texture.name = `lake-planar-reflection-${i}`;
    target.texture.colorSpace = THREE.LinearSRGBColorSpace;
  }
  let readTarget = targets[0], writeTarget = targets[1];
  const mirrorCamera = new THREE.PerspectiveCamera();
  const textureMatrix = uniforms.reflectionMatrix.value, captureMatrix = new THREE.Matrix4();
  const bias = new THREE.Matrix4().set(.5, 0, 0, .5, 0, .5, 0, .5, 0, 0, .5, .5, 0, 0, 0, 1);
  const size = new THREE.Vector2(), eye = new THREE.Vector3(), look = new THREE.Vector3();
  const direction = new THREE.Vector3(), clip = new THREE.Vector4(), q = new THREE.Vector4();
  const plane = new THREE.Plane(), normal = new THREE.Vector3(0, 1, 0);
  const clearColor = new THREE.Color(), viewport = new THREE.Vector4(), scissor = new THREE.Vector4();
  let tier = -1, disposed = false, rendering = false, needsCapture = true;
  let lastLevel = NaN, lastTime = NaN, lastTimeBin = -1, lastWeatherKey = null;
  const lastCameraMatrix = new THREE.Matrix4(), lastProjection = new THREE.Matrix4();
  uniforms.reflectionTexture.value = readTarget.texture;

  function resize(nextTier = Math.max(0, tier)) {
    if (disposed) return;
    renderer.getDrawingBufferSize(size);
    const longest = nextTier === 0 ? 768 : 512;
    const scale = Math.min(.5, longest / Math.max(size.x, size.y));
    const width = Math.max(64, Math.floor(size.x * scale));
    const height = Math.max(64, Math.floor(size.y * scale));
    if (readTarget.width !== width || readTarget.height !== height) {
      for (const target of targets) target.setSize(width, height);
      uniforms.reflectionEnabled.value = 0;
      uniforms.reflectionTexel.value.set(1 / width, 1 / height);
      needsCapture = true;
    }
  }

  function update(level, qualityTier = 0) {
    if (disposed || rendering) return false;
    const nextTier = Math.max(0, Math.min(2, qualityTier | 0));
    if (nextTier === 2) {
      uniforms.reflectionEnabled.value = 0;
      tier = nextTier;
      needsCapture = true;
      return false;
    }
    if (tier !== nextTier) {
      tier = nextTier;
      resize(tier);
      needsCapture = true;
    }
    camera.updateMatrixWorld();
    eye.setFromMatrixPosition(camera.matrixWorld);
    if (eye.y <= level + .012) {
      uniforms.reflectionEnabled.value = 0;
      needsCapture = true;
      return false;
    }
    const moving = !camera.matrixWorld.equals(lastCameraMatrix)
      || !camera.projectionMatrix.equals(lastProjection) || level !== lastLevel;
    const time = uniforms.time?.value ?? 0;
    const timeBin = Math.floor((time + 1e-7) * 15);
    const sun = uniforms.sun?.value, wind = uniforms.wind?.value;
    const weatherKey = [uniforms.rain?.value, uniforms.snow?.value,
      uniforms.storm?.value, uniforms.lightning?.value, uniforms.waterDaylight?.value,
      sun?.x, sun?.y, sun?.z, wind?.x, wind?.y].join('|');
    const changedState = weatherKey !== lastWeatherKey || renderer.shadowMap.needsUpdate;
    // High renders every new state, so arbitrary equal-time captures agree at
    // 30 / 60 / 120 Hz. Medium intentionally trades moving-reflection cadence
    // for cost (15 Hz), while camera / weather / asset changes stay immediate.
    const changedTime = tier === 0 ? time !== lastTime : timeBin !== lastTimeBin;
    if (!needsCapture && !moving && !changedState && !changedTime) return false;
    rendering = true;
    needsCapture = true;
    camera.getWorldDirection(direction);
    look.copy(eye).add(direction);
    eye.y = 2 * level - eye.y;
    look.y = 2 * level - look.y;
    mirrorCamera.position.copy(eye);
    mirrorCamera.up.copy(camera.up);
    mirrorCamera.up.y *= -1;
    mirrorCamera.lookAt(look);
    mirrorCamera.near = camera.near;
    mirrorCamera.far = camera.far;
    mirrorCamera.layers.mask = camera.layers.mask;
    mirrorCamera.projectionMatrix.copy(camera.projectionMatrix);
    mirrorCamera.updateMatrixWorld();
    // The texture projection uses the ordinary mirror projection. The oblique
    // clip only modifies its depth row, so sampling remains exactly aligned.
    captureMatrix.copy(bias).multiply(mirrorCamera.projectionMatrix).multiply(mirrorCamera.matrixWorldInverse);
    plane.setFromNormalAndCoplanarPoint(normal, new THREE.Vector3(0, level + .008, 0));
    plane.applyMatrix4(mirrorCamera.matrixWorldInverse);
    clip.set(plane.normal.x, plane.normal.y, plane.normal.z, plane.constant);
    const p = mirrorCamera.projectionMatrix.elements;
    q.set((Math.sign(clip.x) + p[8]) / p[0], (Math.sign(clip.y) + p[9]) / p[5], -1, (1 + p[10]) / p[14]);
    clip.multiplyScalar(2 / clip.dot(q));
    p[2] = clip.x; p[6] = clip.y; p[10] = clip.z + 1 - .003; p[14] = clip.w;
    mirrorCamera.projectionMatrixInverse.copy(mirrorCamera.projectionMatrix).invert();

    const hidden = new Map();
    for (const object of exclude) if (object) { hidden.set(object, object.visible); object.visible = false; }
    const oldTarget = renderer.getRenderTarget();
    const oldFace = renderer.getActiveCubeFace();
    const oldMip = renderer.getActiveMipmapLevel();
    const oldBackground = scene.background;
    const oldAutoClear = renderer.autoClear;
    const oldClearAlpha = renderer.getClearAlpha();
    const oldXR = renderer.xr.enabled;
    const oldShadowAuto = renderer.shadowMap.autoUpdate;
    const oldShadowNeeds = renderer.shadowMap.needsUpdate;
    const oldScissorTest = renderer.getScissorTest();
    renderer.getClearColor(clearColor);
    renderer.getViewport(viewport);
    renderer.getScissor(scissor);
    let captured = false;
    const frameBefore = renderer.info?.render?.frame;
    try {
      scene.background = null;
      renderer.autoClear = true;
      renderer.xr.enabled = false;
      renderer.shadowMap.autoUpdate = false;
      // A pending shadow refresh must happen before this image samples it.
      // The successful reflected render consumes it once for the main view too.
      renderer.shadowMap.needsUpdate = oldShadowNeeds;
      renderer.setClearColor(0x000000, 0);
      renderer.setRenderTarget(writeTarget);
      renderer.setScissorTest(false);
      renderer.clear(true, true, false);
      renderer.render(scene, mirrorCamera);
      captured = frameBefore === undefined || renderer.info.render.frame > frameBefore;
      if (captured) {
        [readTarget, writeTarget] = [writeTarget, readTarget];
        uniforms.reflectionTexture.value = readTarget.texture;
        textureMatrix.copy(captureMatrix);
        uniforms.reflectionEnabled.value = 1;
        lastCameraMatrix.copy(camera.matrixWorld);
        lastProjection.copy(camera.projectionMatrix);
        lastLevel = level;
        lastTime = time;
        lastTimeBin = timeBin;
        lastWeatherKey = weatherKey;
        needsCapture = false;
      }
    } finally {
      for (const [object, visible] of hidden) object.visible = visible;
      scene.background = oldBackground;
      renderer.autoClear = oldAutoClear;
      renderer.xr.enabled = oldXR;
      renderer.shadowMap.autoUpdate = oldShadowAuto;
      renderer.shadowMap.needsUpdate = captured ? false : oldShadowNeeds;
      renderer.setClearColor(clearColor, oldClearAlpha);
      renderer.setRenderTarget(oldTarget, oldFace, oldMip);
      renderer.setViewport(viewport);
      renderer.setScissor(scissor);
      renderer.setScissorTest(oldScissorTest);
      rendering = false;
    }
    return captured;
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    uniforms.reflectionEnabled.value = 0;
    if (targets.some(target => uniforms.reflectionTexture.value === target.texture)) uniforms.reflectionTexture.value = null;
    for (const target of targets) target.dispose();
  }
  return {update, resize, dispose, get target() {return readTarget;}, targets, camera: mirrorCamera};
}
